import { useState, useEffect, useRef, useCallback } from 'react';

interface BrandBannerSliderProps {
  onCategoryClick?: (cat: string) => void;
}

const scentItems = [
  { icon: 'ri-flower-line', ko: '플로럴', zh: '花香系' },
  { icon: 'ri-leaf-line', ko: '프레시', zh: '清新系' },
  { icon: 'ri-tree-line', ko: '우디', zh: '木香系' },
  { icon: 'ri-heart-line', ko: '소프트', zh: '柔和系' },
];

const snackTags = [
  { icon: 'ri-seedling-line', ko: '아몬드', zh: '扁桃仁' },
  { icon: 'ri-seedling-line', ko: '땅콩', zh: '花生仁' },
  { icon: 'ri-restaurant-line', ko: '믹스넛', zh: '混合坚果' },
  { icon: 'ri-heart-pulse-line', ko: '프로틴바', zh: '蛋白棒' },
];

const hbafImages = [
  'https://i.ibb.co/Tqx6RXWC/1.jpg',
  'https://i.ibb.co/q3NZtM4S/2.jpg',
  'https://i.ibb.co/Kp1xRdXQ/3.jpg',
  'https://i.ibb.co/RTmgK4Bf/4.jpg',
  'https://i.ibb.co/b5hBK1b6/5.jpg',
  'https://i.ibb.co/fzg8YhqD/7.jpg',
];

const dressroomImages = [
  'https://wdressroom.co.kr/web/product/medium/202508/d57e1ba1c2e154a0025a75129fa20ea0.jpg',
  'https://wdressroom.co.kr/web/product/medium/202401/93e856024d74db29348ca085a7cd0d26.jpg',
  'https://wdressroom.co.kr/web/product/medium/202504/eb901c55640e5bed1a20c2413d038a7c.jpg',
  'https://wdressroom.co.kr/web/product/medium/202601/6f53706e3ee2b33d582d858deb78a3e4.jpg',
  'https://wdressroom.co.kr/web/product/medium/202601/e13e1ae280891fcc009dea804dac231d.jpg',
  'https://wdressroom.co.kr/web/product/medium/202601/563fcc99ce43d3d4bb5a9c2c5caee72b.jpg',
];

const AUTO_PLAY_INTERVAL = 5000;

export default function BrandBannerSlider({ onCategoryClick }: BrandBannerSliderProps) {
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const dragStartX = useRef<number | null>(null);
  const dragStartTime = useRef<number | null>(null);
  const totalSlides = 2;

  const goTo = useCallback((index: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrent(index);
    setTimeout(() => setIsAnimating(false), 600);
  }, [isAnimating]);

  const goNext = useCallback(() => {
    goTo((current + 1) % totalSlides);
  }, [current, goTo]);

  const goPrev = useCallback(() => {
    goTo((current - 1 + totalSlides) % totalSlides);
  }, [current, goTo]);

  // 자동 재생
  useEffect(() => {
    if (isPaused) return;
    timerRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % totalSlides);
    }, AUTO_PLAY_INTERVAL);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPaused]);

  // 드래그/스와이프 처리
  const handleDragStart = (clientX: number) => {
    dragStartX.current = clientX;
    dragStartTime.current = Date.now();
    setIsPaused(true);
  };

  const handleDragEnd = (clientX: number) => {
    if (dragStartX.current === null) return;
    const diff = dragStartX.current - clientX;
    const duration = Date.now() - (dragStartTime.current ?? 0);
    const threshold = duration < 300 ? 30 : 80;
    if (Math.abs(diff) > threshold) {
      if (diff > 0) goNext();
      else goPrev();
    }
    dragStartX.current = null;
    dragStartTime.current = null;
    setTimeout(() => setIsPaused(false), 1000);
  };

  const handleMouseDown = (e: React.MouseEvent) => handleDragStart(e.clientX);
  const handleMouseUp = (e: React.MouseEvent) => handleDragEnd(e.clientX);
  const handleTouchStart = (e: React.TouchEvent) => handleDragStart(e.touches[0].clientX);
  const handleTouchEnd = (e: React.TouchEvent) => handleDragEnd(e.changedTouches[0].clientX);

  const handleBannerClick = (category: string) => {
    if (onCategoryClick) onCategoryClick(category);
    const el = document.getElementById('categories');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      className="w-full px-4 md:px-8 py-10 md:py-14"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div
        className="relative rounded-3xl overflow-hidden select-none"
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        style={{ cursor: 'grab' }}
      >
        {/* 슬라이드 트랙 */}
        <div
          className="flex transition-transform duration-700 ease-in-out"
          style={{ transform: `translateX(-${current * 100}%)` }}
        >
          {/* ── 슬라이드 1: 드레스룸 섬유향수 ── */}
          <div
            className="w-full shrink-0 relative cursor-pointer group"
            onClick={() => handleBannerClick('섬유향수')}
            style={{ background: 'linear-gradient(135deg, #1C1C1C 0%, #2A2218 50%, #1C1C1C 100%)' }}
          >
            <div className="absolute inset-0 opacity-30">
              <img
                src="https://wdressroom.co.kr/web/upload/goodymall/kr/main/mainImg210.jpg"
                alt="드레스룸 섬유향수"
                className="w-full h-full object-cover object-center"
              />
            </div>
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(90deg, rgba(28,28,28,0.95) 0%, rgba(28,28,28,0.7) 50%, rgba(28,28,28,0.4) 100%)' }}
            />
            <div className="relative z-10 flex flex-col lg:flex-row items-center gap-8 p-8 md:p-12">
              <div className="flex-1 text-left">
                <div className="flex items-center gap-2 mb-4">
                  <span
                    className="text-[10px] font-black tracking-[0.3em] uppercase px-3 py-1 rounded-full"
                    style={{ backgroundColor: '#C8B89A', color: '#1C1C1C' }}
                  >
                    NEW CATEGORY
                  </span>
                  <span className="text-[10px] font-bold tracking-[0.2em]" style={{ color: '#7A6A5A' }}>新品上架 / 신규 입점</span>
                </div>
                <div className="mb-3">
                  <img
                    src="https://wdressroom.co.kr/web/upload/goodymall/kr/main/flogo.png"
                    alt="W.DRESSROOM"
                    className="h-7 object-contain brightness-0 invert opacity-90 mb-2"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                  <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-white leading-tight tracking-tight">
                    衣物香水系列
                  </h2>
                  <p className="text-base md:text-lg font-bold mt-1" style={{ color: '#C8B89A' }}>
                    섬유향수 컬렉션 · Fabric Perfume
                  </p>
                </div>
                <p className="text-[13px] md:text-[14px] leading-relaxed mb-2" style={{ color: '#A09080' }}>
                  超越喷洒在衣物上的香水，为日常所有织物增添香气的全新生活方式
                </p>
                <p className="text-[12px] leading-relaxed mb-6" style={{ color: '#5A4A3A' }}>
                  옷에 뿌리는 향수를 넘어, 일상의 모든 섬유에 향기를 더하는 새로운 라이프스타일
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {scentItems.map((item) => (
                    <span
                      key={item.ko}
                      className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full font-medium"
                      style={{ backgroundColor: 'rgba(200,184,154,0.12)', color: '#C8B89A', border: '1px solid rgba(200,184,154,0.2)' }}
                    >
                      <i className={`${item.icon} text-[12px]`}></i>
                      {item.ko}
                      <span style={{ color: '#5A4A3A' }}>/ {item.zh}</span>
                    </span>
                  ))}
                </div>
                <button
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-black text-[13px] transition-all duration-200 group-hover:gap-3 whitespace-nowrap cursor-pointer"
                  style={{ backgroundColor: '#C8B89A', color: '#1C1C1C' }}
                >
                  <i className="ri-shirt-line"></i>
                  查看全部衣物香水
                  <span className="font-normal text-[11px] opacity-70">/ 섬유향수 전체 보기</span>
                  <i className="ri-arrow-right-line transition-transform duration-200 group-hover:translate-x-1"></i>
                </button>
              </div>
              <div className="hidden lg:grid grid-cols-3 gap-3 w-80 shrink-0">
                {dressroomImages.map((src, i) => (
                  <div
                    key={i}
                    className="aspect-square rounded-xl overflow-hidden"
                    style={{ border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    <img
                      src={src}
                      alt={`드레스룸 섬유향수 ${i + 1}`}
                      className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="relative z-10 border-t flex" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
              {[
                { value: '20+', label: '향 라인업', labelZh: '香气种类' },
                { value: '80ml~160ml', label: '다양한 용량', labelZh: '多种容量' },
                { value: '섬유 전용', label: '특수 포뮬러', labelZh: '专用配方' },
                { value: 'B2B', label: '도매 공급', labelZh: '批发供应' },
              ].map((stat, idx) => (
                <div
                  key={stat.label}
                  className="flex-1 text-center py-4 px-2"
                  style={{ borderLeft: idx > 0 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}
                >
                  <p className="text-[13px] md:text-[14px] font-black text-white">{stat.value}</p>
                  <p className="text-[10px] md:text-[11px] mt-0.5" style={{ color: '#7A6A5A' }}>{stat.label} / {stat.labelZh}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── 슬라이드 2: HBAF 스낵 ── */}
          <div
            className="w-full shrink-0 relative cursor-pointer group"
            onClick={() => handleBannerClick('스낵·김')}
            style={{ background: 'linear-gradient(135deg, #1A1400 0%, #2A2000 40%, #1A1400 100%)' }}
          >
            <div className="absolute inset-0 opacity-25">
              <img
                src="https://cdn.imweb.me/thumbnail/20230728/e48176aa95fcb.png"
                alt="HBAF 스낵"
                className="w-full h-full object-cover object-center"
              />
            </div>
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(90deg, rgba(26,20,0,0.97) 0%, rgba(26,20,0,0.75) 50%, rgba(26,20,0,0.45) 100%)' }}
            />
            <div className="relative z-10 flex flex-col lg:flex-row items-center gap-8 p-8 md:p-12">
              <div className="flex-1 text-left">
                <div className="flex items-center gap-2 mb-4">
                  <span
                    className="text-[10px] font-black tracking-[0.3em] uppercase px-3 py-1 rounded-full"
                    style={{ backgroundColor: '#E6A800', color: '#1A1400' }}
                  >
                    KOREA&apos;S LARGEST NUT BRAND
                  </span>
                  <span className="text-[10px] font-bold tracking-[0.2em]" style={{ color: '#7A6A00' }}>
                    韩国最大坚果品牌 / 한국 최대 견과류 브랜드
                  </span>
                </div>
                <div className="mb-3">
                  <h2
                    className="text-3xl md:text-4xl lg:text-5xl font-black leading-tight tracking-tight"
                    style={{ color: '#E6A800', fontFamily: 'Impact, Arial Black, sans-serif' }}
                  >
                    HBAF
                  </h2>
                  <p className="text-base md:text-lg font-bold mt-1 text-white">
                    零食·坚果系列
                  </p>
                  <p className="text-sm font-semibold mt-0.5" style={{ color: '#E6A800' }}>
                    스낵·견과류 컬렉션 · Healthy But Awesome Flavors
                  </p>
                </div>
                <p className="text-[13px] md:text-[14px] leading-relaxed mb-2" style={{ color: '#A09000' }}>
                  健康而美味！拥有40年传统的韩国优质坚果品牌
                </p>
                <p className="text-[12px] leading-relaxed mb-6" style={{ color: '#5A5000' }}>
                  건강하면서도 맛있는 것! 40년 전통의 프리미엄 견과류 브랜드
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {snackTags.map((item) => (
                    <span
                      key={item.ko}
                      className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full font-medium"
                      style={{ backgroundColor: 'rgba(230,168,0,0.12)', color: '#E6A800', border: '1px solid rgba(230,168,0,0.2)' }}
                    >
                      <i className={`${item.icon} text-[12px]`}></i>
                      {item.ko}
                      <span style={{ color: '#7A6A00' }}>/ {item.zh}</span>
                    </span>
                  ))}
                </div>
                <button
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-black text-[13px] transition-all duration-200 group-hover:gap-3 whitespace-nowrap cursor-pointer"
                  style={{ backgroundColor: '#E6A800', color: '#1A1400' }}
                >
                  <i className="ri-store-2-line"></i>
                  查看全部零食·海苔
                  <span className="font-normal text-[11px] opacity-70">/ 스낵·김 전체 보기</span>
                  <i className="ri-arrow-right-line transition-transform duration-200 group-hover:translate-x-1"></i>
                </button>
              </div>
              <div className="hidden lg:grid grid-cols-3 gap-3 w-80 shrink-0">
                {hbafImages.map((src, i) => (
                  <div
                    key={i}
                    className="aspect-square rounded-xl overflow-hidden bg-white/5"
                    style={{ border: '1px solid rgba(230,168,0,0.15)' }}
                  >
                    <img
                      src={src}
                      alt={`HBAF 스낵 ${i + 1}`}
                      className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="relative z-10 border-t flex" style={{ borderColor: 'rgba(230,168,0,0.1)' }}>
              {[
                { value: '8+', label: '상품 라인업', labelZh: '产品阵容' },
                { value: '10g~80g', label: '다양한 용량', labelZh: '多种规格' },
                { value: '상온 보관', label: '간편 보관', labelZh: '常温储存' },
                { value: 'B2B', label: '도매 공급', labelZh: '批发供应' },
              ].map((stat, idx) => (
                <div
                  key={stat.label}
                  className="flex-1 text-center py-4 px-2"
                  style={{ borderLeft: idx > 0 ? '1px solid rgba(230,168,0,0.1)' : 'none' }}
                >
                  <p className="text-[13px] md:text-[14px] font-black" style={{ color: '#E6A800' }}>{stat.value}</p>
                  <p className="text-[10px] md:text-[11px] mt-0.5" style={{ color: '#7A6A00' }}>
                    {stat.label} / {stat.labelZh}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 좌우 화살표 버튼 */}
        <button
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 cursor-pointer"
          style={{ backgroundColor: 'rgba(255,255,255,0.12)', color: 'white', border: '1px solid rgba(255,255,255,0.15)' }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.22)')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)')}
        >
          <i className="ri-arrow-left-s-line text-lg"></i>
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 cursor-pointer"
          style={{ backgroundColor: 'rgba(255,255,255,0.12)', color: 'white', border: '1px solid rgba(255,255,255,0.15)' }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.22)')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)')}
        >
          <i className="ri-arrow-right-s-line text-lg"></i>
        </button>

        {/* 하단 인디케이터 도트 */}
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
          {Array.from({ length: totalSlides }).map((_, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); goTo(i); }}
              className="transition-all duration-300 rounded-full cursor-pointer"
              style={{
                width: i === current ? '24px' : '8px',
                height: '8px',
                backgroundColor: i === current
                  ? (current === 0 ? '#C8B89A' : '#E6A800')
                  : 'rgba(255,255,255,0.3)',
              }}
            />
          ))}
        </div>

        {/* 진행 바 */}
        <div
          className="absolute bottom-0 left-0 h-[3px] z-20 transition-all"
          style={{
            width: isPaused ? '0%' : '100%',
            backgroundColor: current === 0 ? '#C8B89A' : '#E6A800',
            transition: isPaused ? 'none' : `width ${AUTO_PLAY_INTERVAL}ms linear`,
          }}
          key={`${current}-${isPaused}`}
        />

        {/* 슬라이드 번호 표시 */}
        <div className="absolute top-4 right-16 z-20 flex items-center gap-1.5">
          <span className="text-[13px] font-black text-white">{current + 1}</span>
          <span className="text-[11px] text-white/40">/</span>
          <span className="text-[11px] text-white/40">{totalSlides}</span>
        </div>
      </div>
    </section>
  );
}
